import cn from "classnames";
import { Transition } from "components/shared/column";
import Pagination from "components/shared/reactTable/pagination";
import differenceInSeconds from "date-fns/differenceInSeconds";
import { accessPermitRelationTypesEnum } from "helpers/enums";
import { viewContainer } from "helpers/redux";
import { ymEvent } from "helpers/system";
import { useLocalStorage } from "hooks/useLocalStorage";
import AngleDown from "icons/planning/angle-down.svg";
import AngleUp from "icons/planning/angle-up.svg";
import Funnel from "icons/planning/funnel.svg";
import Reload from "icons/planning/reload.svg";
import { UmamiManager } from "lib/umami";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { getInfoList } from "sources/books/accessPermits";
import { increase } from "sources/counter";
import { showBidCarsPlanningSettings } from "sources/profile/organization";
import { getReport as getTripPlanningData } from "sources/tripPlanning";
import Filter from "./filter";
import Head from "./head";
import Table from "./table";
import { buildFilter } from "./utils";

function TripPlanning(props) {
    const location = useLocation();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [dataLoadedAt, setDataLoadedAt] = useState();
    const [sorted, setSorted] = useLocalStorage("trip-planning-sorted", {
        column: "plannedEndDate",
        field: "estimatedEndDate",
        direction: "desc",
    });
    const [filtered, setFiltered] = useLocalStorage("trip-planning-filtered", {});
    const [filterOpened, setFilterOpened] = useState(false);
    const [accessPermits, setAccessPermits] = useState([]);
    const [start, setStart] = useState(0);
    const [length, setLength] = useLocalStorage("trip-planning-length", 15);
    const [itemsCount, setItemsCount] = useState(0);

    const updateFrequencyRef = useRef(5 * 60); // default 5 min
    const refreshTimerRef = useRef(null);
    const loadDataRef = useRef();

    const loadData = () => {
        setLoading(true);
        const orderBy = sorted.field ? `${sorted.field} ${sorted.direction}` : undefined;
        const { onlyMyCars, ...rest } = filtered;

        const filter = buildFilter(rest);

        return getTripPlanningData(
            { start, length, orderBy, filter },
            {
                onlyMyCars: Boolean(onlyMyCars?.value),
            },
        ).then((res) => {
            setData(res.items);
            setLoading(false);
            setDataLoadedAt(Date.now());
            setItemsCount(res.carsCount);
        });
    };

    useEffect(() => {
        loadDataRef.current = loadData;
    }, [loadData]);

    useEffect(() => {
        ymEvent("TripPlanningOpened");
        increase({ Name: "PlanningOpens" });
        getInfoList({ preparedFilter: { relationType: { ne: accessPermitRelationTypesEnum.NoRelation } } }).then(
            (res) => res && setAccessPermits(res.data),
        );
        showBidCarsPlanningSettings().then((res) => {
            updateFrequencyRef.current = (res.updateFrequency || 5) * 60;
            createInterval();
            loadData();
        });
        window.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            clearInterval(refreshTimerRef.current);
            window.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    useEffect(() => {
        if (loading) {
            return;
        }
        loadData();
    }, [start, length, sorted, filtered]);

    const createInterval = () => {
        if (document.visibilityState === "hidden") {
            return;
        }
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = setInterval(() => {
            if (loadDataRef.current) {
                loadDataRef.current();
            }
        }, updateFrequencyRef.current * 1000);
    };

    const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            clearInterval(refreshTimerRef.current);
        } else {
            if (dataLoadedAt && differenceInSeconds(new Date(), new Date(dataLoadedAt)) > updateFrequencyRef.current) {
                loadData();
            }
            createInterval();
        }
    };

    const resetFilters = () => {
        setFiltered({});
        setSorted({});
    };

    const onFilteredChange = ({ id, value, ...rest }) => {
        const hasValue = value && (!Array.isArray(value) || value.length > 0);

        if (hasValue) {
            ymEvent("TripPlanningFilterUsed");
        }

        setFiltered((state) => {
            const newState = { ...state };
            if (!hasValue) {
                delete newState[id];
            } else {
                newState[id] = { id, value, ...rest };
            }
            return newState;
        });
        setStart(0);
    };

    const onSortedChange = (column, field, dir) => {
        const direction = dir ?? (sorted.field === field && sorted.direction === "asc" ? "desc" : "asc");

        ymEvent("TripPlanningTableUsed");
        setSorted({
            column: column.id,
            field,
            direction,
        });
    };

    const changePage = (page) => setStart(page * length);

    const changePageSize = (pageSize) => {
        setStart(0);
        setLength(pageSize);
    };

    const paginationProps = {
        page: start / length,
        pages: Math.ceil(itemsCount / length),
        pageSize: length,
        onPageChange: changePage,
        onPageSizeChange: changePageSize,
        pageSizeOptions: [15, 30, 50, 100],
    };

    const handleChangeFilterOpened = () => {
        UmamiManager.track("Planing3FilterButtonClicked");

        if (!filterOpened) {
            ymEvent("TripPlanningFilterUsed");
        }
        setFilterOpened((opened) => !opened);
    };

    const selectedFiltersCount = Object.keys(filtered).length;

    return (
        <div className="page-content">
            <Transition dir="full" {...{ location, history }}>
                <div className="page-book__show book-show">
                    <Head dataLoadedAt={dataLoadedAt}>
                        <div className="d-flex justify-content-end align-items-center">
                            {/* <div className="planning-tabs"> */}
                            {/* <div className="planning-tabs__item planning-tabs__item--active">Данные по ТС</div>
                    <div className="planning-tabs__item">Заказы</div> */}
                            {/* </div> */}
                            <div className="d-flex">
                                <div
                                    className={cn("planning-btn mr-2", { "planning-btn--active": filterOpened })}
                                    onClick={handleChangeFilterOpened}
                                >
                                    <Funnel />
                                    Фильтр
                                    {filterOpened ? <AngleUp /> : <AngleDown />}
                                    {selectedFiltersCount > 0 && (
                                        <div className="planning-btn__badge">{selectedFiltersCount}</div>
                                    )}
                                </div>
                                <div
                                    className="planning-btn"
                                    onClick={() => {
                                        ymEvent("TripPlanningManualRefreshClicked");
                                        loadData();
                                    }}
                                >
                                    <Reload />
                                    Обновить
                                </div>
                            </div>
                        </div>
                        {filterOpened && (
                            <Filter
                                resetFilters={resetFilters}
                                filtered={filtered}
                                onFilteredChange={onFilteredChange}
                            />
                        )}
                    </Head>
                    <div className="book-show__body">
                        <div className={cn("book-list h-100", { loading })}>
                            <div className="d-flex flex-column h-100">
                                <Table
                                    {...{
                                        data,
                                        sorted,
                                        filtered,
                                        onFilteredChange,
                                        onSortedChange,
                                        roles: props.roles,
                                        organization: props.organization,
                                        loading,
                                        start,
                                        accessPermits,
                                        modal: props.modal,
                                    }}
                                />
                                <div className="p-table__foot">
                                    <Pagination isNotExportable {...paginationProps} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    );
}

export default viewContainer(TripPlanning, "planning");
